import { flipDelayFraction, type AnimCell } from "./mapSoilAnimation";
import { EXCLUSION_BITS } from "./animPlanes";

export const GRID_BEATS = [
  {
    key: "waterway",
    label: "Waterways",
    bits: (1 << EXCLUSION_BITS.waterway) | (1 << EXCLUSION_BITS.waterwayBuffer),
  },
  {
    key: "boundary",
    label: "Distance from Boundary",
    bits: 1 << EXCLUSION_BITS.boundary,
  },
  {
    key: "insideCorner",
    label: "Inside Corners",
    bits: 1 << EXCLUSION_BITS.insideCorner,
  },
] as const;

/* * Which overlay is running at `t`, how far through it, and everything masked SO FAR. */
export function gridBeatAt(
  t: number,
  spanMs: number,
): { index: number; label: string; within: number; mask: number } {
  const per = spanMs / GRID_BEATS.length;
  const i = Math.min(GRID_BEATS.length - 1, Math.max(0, Math.floor(t / per)));
  let mask = 0;
  // CUMULATIVE, which is what "intersection" means here: every earlier rule keeps applying, so the
  // green shrinks monotonically to the area the sampler actually had left.
  for (let k = 0; k <= i; k++) mask |= GRID_BEATS[k]!.bits;
  return {
    index: i,
    label: GRID_BEATS[i]!.label,
    within: Math.min(1, (t - i * per) / per),
    mask,
  };
}

/*
 *
 * `null` means the plane has no answer here, and painting it green would claim the sampler could
 * place a point somewhere it was never told about.
 */
export function excludedBy(
  bits: number | null | undefined,
  mask: number,
): boolean | null {
  if (bits == null) return null;
  return (bits & mask) !== 0;
}

/*
 *
 * Has this cell's overlay reached it yet, within a beat that runs 0→1?
 *
 * The same spiral ordering as every other animation here — one definition of "outward from the centre"
 * so the four preset animations move the same way.
 */
export function overlayIn(cell: AnimCell, within: number): boolean {
  return within >= flipDelayFraction(cell);
}

/* * Still available to sample. */
export const GRID_CLEAR = "#22c55e";
/* * Excluded by a rule that has already been overlaid. */
export const GRID_DARK = "#0b1220";
